'use client';

import { useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  buildAccountsSignInUrl,
  safeRedirectTarget,
} from '@/lib/auth/client-constants';
import { ensureValidToken } from '@/lib/auth/client-refresh';
import { decodeJwtPayload, hashToken } from '@/lib/auth/decode-jwt';
import { installAuthAxiosInterceptor } from '@/lib/auth/install-axios-interceptor';
import type { AuthUser, SyncResponse } from '@/lib/auth/types';
import { scheduleParamRemoval } from '@/lib/url-cleaner';
import {
  clearAuthCache,
  readAuthCache,
  useNewAuthStore,
  writeAuthCache,
} from '@/store/use-new-auth-store';

const COOKIE_TOKEN = 'token';

interface NewAuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (redirectTo?: string) => void;
  logout: () => Promise<void>;
}

const NewAuthContext = createContext<NewAuthContextValue | null>(null);

export function NewAuthProvider({ children }: { children: ReactNode }) {
  const user = useNewAuthStore.use.user();
  const isAuthenticated = useNewAuthStore.use.isAuthenticated();
  const isLoading = useNewAuthStore.use.isLoading();

  const setUser = useNewAuthStore.use.setUser();
  const setCustomerId = useNewAuthStore.use.setCustomerId();
  const setIsLoading = useNewAuthStore.use.setIsLoading();
  const resetStore = useNewAuthStore.use.logout();

  const searchParams = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const initRanRef = useRef(false);

  /* ---------- Hydrate from cookie + optimistic JWT decode ---------- */
  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;

    installAuthAxiosInterceptor();
    cleanupLegacyArtifacts();

    void hydrateAndSync({
      tokenFromUrl,
      setUser,
      setCustomerId,
      setIsLoading,
      resetStore,
    });
  }, [tokenFromUrl, setUser, setCustomerId, setIsLoading, resetStore]);

  /* ---------- Login: bounce ra accounts với redirect_uri = current page ----------
   * Accounts set cookies `token` + `refresh_token` qua Set-Cookie header, user
   * land thẳng về current page → hydrateAndSync ở provider tự đọc cookie + sync BE.
   * Không cần callback page riêng. */
  const login = useCallback((redirectTo?: string) => {
    try {
      const target =
        redirectTo ?? window.location.pathname + window.location.search;
      const safeTarget = safeRedirectTarget(target);
      const absoluteTarget = `${window.location.origin}${safeTarget}`;
      window.location.href = buildAccountsSignInUrl(absoluteTarget);
    } catch (err) {
      console.error('login failed', err);
    }
  }, []);

  /* ---------- Logout: API clear cookies + reset store + hard nav ---------- */
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch {
      /* ignore */
    }
    resetStore();
    clearAuthCache();
    window.location.href = '/';
  }, [resetStore]);

  const value = useMemo<NewAuthContextValue>(
    () => ({ user, isAuthenticated, isLoading, login, logout }),
    [user, isAuthenticated, isLoading, login, logout]
  );

  return (
    <NewAuthContext.Provider value={value}>{children}</NewAuthContext.Provider>
  );
}

export function useNewAuth() {
  const ctx = useContext(NewAuthContext);
  if (!ctx) throw new Error('useNewAuth must be used within NewAuthProvider');
  return ctx;
}

/* ---------------- Hydrate logic ---------------- */

interface HydrateOpts {
  tokenFromUrl: string | null;
  setUser: (u: AuthUser | null) => void;
  setCustomerId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  resetStore: () => void;
}

async function hydrateAndSync({
  tokenFromUrl,
  setUser,
  setCustomerId,
  setIsLoading,
  resetStore,
}: HydrateOpts) {
  // 1. WebInApp: nếu có ?token=, set cookie trước khi đọc lại
  if (tokenFromUrl) {
    setTokenCookieFromUrl(tokenFromUrl);
    clearTokenFromUrl();
  }

  // 2. Pre-emptive refresh nếu token sắp expire (< 30s) — đảm bảo BE call
  //    đầu tiên (sync) không bị 401 retry storm.
  const token = await ensureValidToken();
  if (!token) {
    // Cookie missing (user xóa tay, hoặc refresh fail) — clear stale persisted
    // state để UI hiện "Chưa đăng nhập" đúng.
    resetStore();
    clearAuthCache();
    setIsLoading(false);
    return;
  }

  // 3. Optimistic — decode JWT, set user state ngay
  const claims = decodeJwtPayload(token);
  if (!claims) {
    setIsLoading(false);
    return;
  }

  const cache = readAuthCache();
  const currentHash = await hashToken(token);

  setUser({
    id: claims.sub,
    phone: claims.cid,
    phoneNumber: claims.cid,
    name: claims.noc,
    email: claims.eoc,
    provider: 'ahamove',
    loginType: 'ahamove',
    systemToken: token,
    cunstomerId:
      cache && cache.tokenHash === currentHash ? cache.customerId : undefined,
  });
  // User state đã có → UI có thể render authenticated ngay (không flash)
  setIsLoading(false);

  // 3. Skip sync nếu hash khớp cache (đã sync trong 7 ngày)
  if (cache && cache.tokenHash === currentHash) {
    return;
  }

  // 4. Background sync (không block UI — isLoading đã false)
  try {
    const res = await fetch('/api/auth/sync', { method: 'POST' });
    if (res.status === 401) {
      resetStore();
      clearAuthCache();
      await fetch('/api/auth/logout', { method: 'POST' });
      return;
    }
    if (!res.ok) return;

    const data = (await res.json()) as SyncResponse;
    if (data.ok) {
      writeAuthCache({
        customerId: data.customer_id,
        tokenHash: currentHash,
        syncedAt: Date.now(),
      });
      setCustomerId(data.customer_id);
    }
  } catch {
    /* network fail → keep state */
  }
}

/**
 * Xóa cookies + localStorage cũ của old auth flow.
 * Chạy 1 lần khi mount NewAuthProvider — đảm bảo no leftover state.
 */
function cleanupLegacyArtifacts() {
  try {
    // Xóa cookie ws_token (cả host-only và domain variant)
    const isProd = process.env.NODE_ENV === 'production';
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `ws_token=; path=/; ${expire}`;
    if (isProd) {
      document.cookie = `ws_token=; path=/; domain=.ahafood.ai; ${expire}`;
    }

    // Xóa zustand old persist key (auth.store) nếu tồn tại
    if (localStorage.getItem('auth.store')) {
      localStorage.removeItem('auth.store');
    }
  } catch {
    /* ignore */
  }
}

function setTokenCookieFromUrl(token: string) {
  // WebInApp: set cookie từ client để route handlers đọc được.
  // Browser side: dùng document.cookie để set với same flags như route.ts.
  const isProd = process.env.NODE_ENV === 'production';
  const parts = [
    `${COOKIE_TOKEN}=${token}`,
    'path=/',
    'SameSite=Lax',
    `max-age=${7 * 24 * 60 * 60}`,
  ];
  if (isProd) parts.push('domain=.ahafood.ai', 'Secure');
  document.cookie = parts.join('; ');
}

function clearTokenFromUrl() {
  scheduleParamRemoval('token');
}
